// ─────────────────────────────────────────────────────────────────────────
// financialStatementPdfRenderer.js
//
// Renders the Financial Summary tab's "Print Financial Statement /
// Print Financial Summary" output — a standalone A4 print document for
// one or several selected contractors.
//
// Architectural contract (per the approved audit):
//   - Reuses subcontractReportPdfRenderer.js's own building blocks
//     (detailCard, variationBlock, logoSvg, buildCss, sectionHeader,
//     fmt/fmtDate/today/monthYear/escapeHtml) verbatim — this file adds
//     NO new visual language, NO new financial formatting, NO second
//     design system. It only assembles those existing blocks into a
//     document with a different title/structure appropriate for a
//     Financial Statement (vs. the full multi-section Subcontract
//     Report).
//   - Consumes `rows` already computed by the SAME pipeline
//     (computeContractFinancials() + buildVariationChain(), both
//     unchanged) that the on-screen Financial Summary itself uses — see
//     buildFinancialStatementPrintData() in Subcontracts.jsx for how
//     those rows are assembled for ONLY the selected contractor IDs.
//   - Same window.open() + static-HTML print architecture already
//     proven for SubcontractReport.jsx: no DOM cloning, no outerHTML,
//     no screenshot rendering.
// ─────────────────────────────────────────────────────────────────────────

import {
  fmt, fmtDate, today, monthYear, escapeHtml,
  variationBlock, detailCard, sectionHeader, logoSvg, buildCss,
} from './subcontractReportPdfRenderer'

/**
 * @param {Array} rows - SAME shape as buildSubcontractReportRows()
 *   output (subcontractReportData.js) — id, contract_type, contract_no,
 *   contractor_name, status, contract_date, practical_completion_date,
 *   dlp_months, original_value, variations, total_variations,
 *   revised_value, certified, retention_gross/released/held, paid,
 *   outstanding, remaining_commitment — for ONLY the contracts the user
 *   selected. Every financial figure here was computed by
 *   computeContractFinancials()/buildVariationChain(), never
 *   recalculated in this file.
 * @param {{name: string}} project
 * @param {Map<string, {rows: Array, unlinkedTotal: number, totalPaidLinked: number}>} ipcDataByContractId -
 *   keyed by contract id, ONE entry per row in `rows`, produced by
 *   buildIpcStatementRows() (subcontractReportData.js) BEFORE calling
 *   this function — this renderer is a pure, synchronous function and
 *   never queries the database itself, so all IPC data must already be
 *   resolved by the caller (Subcontracts.jsx's
 *   handlePrintFinancialStatement()).
 * @returns {string} complete standalone A4 HTML document
 */
export function renderFinancialStatementPdf(rows, project, ipcDataByContractId) {
  // Defense in depth: the Print button is disabled at zero selection
  // (Subcontracts.jsx), but this function should never crash if called
  // with an empty array regardless — same principle as the main
  // report's empty-state handling.
  if (!rows || rows.length === 0) {
    return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Financial Statement</title>
<style>${buildCss()}</style></head>
<body>${statementHeaderSection(project, { reportTitle: 'FINANCIAL SUMMARY STATEMENT', rowCount: 0, materialCount: 0, equipmentCount: 0, contractorLabel: 'No contractor selected' })}
<section class="section-block"><div class="empty-note">No contracts selected for this statement.</div></section>
</body></html>`
  }
  const isSingle = rows.length === 1
  const reportTitle = isSingle ? 'SUBCONTRACTOR FINANCIAL STATEMENT' : 'FINANCIAL SUMMARY STATEMENT'
  const materialCount = rows.filter(r => r.contract_type === 'material').length
  const equipmentCount = rows.filter(r => r.contract_type === 'equipment').length

  const headerHtml = statementHeaderSection(project, {
    reportTitle, rowCount: rows.length, materialCount, equipmentCount,
    contractorLabel: isSingle
      ? `${rows[0].contractor_name} (${rows[0].contract_no})`
      : `${rows.length} contractors selected`,
  })

  // Consolidated summary table only makes sense for multiple
  // contractors (per the approved spec, Section 4: "Page 1: Report
  // Header, Executive Summary, Consolidated Financial Summary table,
  // then contractor-level sections"). A single-contractor print skips
  // straight to that one contractor's detail card — a one-row summary
  // table would be redundant with the card immediately below it.
  // Always shown (this revision) — previously skipped for a single
  // contractor on the assumption it would be redundant with the detail
  // card below; per updated requirement, it now acts as a summary
  // line ahead of the full detail, same as a one-line invoice summary
  // before its breakdown.
  const summaryTableHtml = consolidatedSummaryTable(rows)

  // Per-contractor block: Contract Header -> Commercial Summary
  // (detailCard, reused verbatim) -> Variations (variationBlock,
  // reused verbatim) -> Detailed IPC Statement (new, this revision) ->
  // reconciliation row. Each contractor is a fully self-contained
  // unit — ipcDataByContractId is keyed by r.id specifically so IPCs
  // from different contractors can never be mixed, and a contractor
  // with no IPC data (map miss) simply shows its Commercial
  // Summary/Variations with no IPC section, never another
  // contractor's rows.
  const [firstRow, ...restRows] = rows
  const detailHtml = `
  <section class="section-block">
    <div class="header-lead-in">
      ${sectionHeader('Contract Statement')}
      ${detailCard(firstRow)}
      ${firstRow.variations.length > 0 ? variationsSubsection([firstRow]) : ''}
      ${ipcStatementSubsection(firstRow, ipcDataByContractId?.get(firstRow.id))}
    </div>
    ${restRows.map(r => `
      ${detailCard(r)}
      ${r.variations.length > 0 ? variationsSubsection([r]) : ''}
      ${ipcStatementSubsection(r, ipcDataByContractId?.get(r.id))}
    `).join('')}
  </section>`

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${escapeHtml(reportTitle)} — ${escapeHtml(project?.name || '')}</title>
<style>${buildCss()}${extraCss()}</style>
</head>
<body>
${headerHtml}
${summaryTableHtml}
${detailHtml}
</body>
</html>`
}

function statementHeaderSection(project, meta) {
  return `
  <section class="report-header">
    <div class="accent-bar"></div>
    <div class="header-top">
      <div class="brand">
        ${logoSvg(30)}
        <div>
          <div class="brand-name">Cost<span class="brand-accent">Space</span></div>
          <div class="brand-tagline">ENTERPRISE COST INTELLIGENCE</div>
        </div>
      </div>
      <div class="header-title-block">
        <div class="header-kicker">${escapeHtml(meta.reportTitle)}</div>
        <div class="header-title">${escapeHtml(project?.name || '')}</div>
      </div>
      <div class="header-date">${today()}</div>
    </div>
    <div class="header-meta-row">
      <div class="header-meta-item"><span class="header-meta-label">CONTRACTOR</span><span class="header-meta-value">${escapeHtml(meta.contractorLabel)}</span></div>
      <div class="header-meta-item"><span class="header-meta-label">SCOPE</span><span class="header-meta-value">${meta.rowCount} contract(s) (${meta.materialCount} Material &middot; ${meta.equipmentCount} Equipment)</span></div>
      <div class="header-meta-item"><span class="header-meta-label">GENERATED</span><span class="header-meta-value">${monthYear()}</span></div>
      <div class="header-meta-item confidential">&#128274; Strictly Confidential</div>
    </div>
  </section>`
}

// Consolidated table for the multi-contractor case — same columns/
// colgroup discipline (explicit widths, no browser auto-division) that
// fixed the Status-column clipping in the main Subcontract Report.
function consolidatedSummaryTable(rows) {
  const totals = rows.reduce((acc, r) => ({
    revised: acc.revised + r.revised_value, certified: acc.certified + r.certified,
    held: acc.held + r.retention_held, paid: acc.paid + r.paid,
    outstanding: acc.outstanding + r.outstanding, remaining: acc.remaining + r.remaining_commitment,
  }), { revised: 0, certified: 0, held: 0, paid: 0, outstanding: 0, remaining: 0 })

  return `
  <section class="section-block">
    ${sectionHeader('Consolidated Financial Summary')}
    <table class="data-table consolidated-table">
      <colgroup>
        <col style="width:4mm"><col style="width:40mm"><col style="width:20mm">
        <col style="width:22mm"><col style="width:22mm"><col style="width:20mm">
        <col style="width:22mm"><col style="width:26mm">
      </colgroup>
      <thead>
        <tr>
          <th class="al">Type</th><th class="al">Contractor</th><th class="al">Contract No.</th>
          <th>Revised Value</th><th>Certified</th><th>Net Retention</th><th>Paid</th><th>Outstanding</th>
        </tr>
      </thead>
      <tbody>
        ${rows.map(r => `
        <tr>
          <td class="al">${r.contract_type === 'equipment' ? '&#128666;' : '&#128230;'}</td>
          <td class="al contractor-cell">${escapeHtml(r.contractor_name)}</td>
          <td class="al mono">${escapeHtml(r.contract_no)}</td>
          <td class="mono">${fmt(r.revised_value)}</td>
          <td class="mono green">${fmt(r.certified)}</td>
          <td class="mono amber">${fmt(r.retention_held)}</td>
          <td class="mono blue">${fmt(r.paid)}</td>
          <td class="mono ${r.outstanding > 0 ? 'red' : ''}">${fmt(r.outstanding)}</td>
        </tr>`).join('')}
      </tbody>
      <tfoot>
        <tr class="totals-row">
          <td colspan="3">Grand Total (${rows.length})</td>
          <td class="mono">${fmt(totals.revised)}</td>
          <td class="mono green">${fmt(totals.certified)}</td>
            <td class="mono amber">${fmt(totals.held)}</td>
            <td class="mono blue">${fmt(totals.paid)}</td>
            <td class="mono ${totals.outstanding > 0 ? 'red' : ''}">${fmt(totals.outstanding)}</td>
          </tr>
        </tfoot>
      </table>
  </section>`
}

// Thin wrapper around variationBlock() — only difference from the main
// report's variationsSection() is the heading text ("Variations /
// Addendums" instead of "Variation Analysis — by Contract"), per the
// approved spec's Section 3 item E. The underlying block/table markup
// is variationBlock() verbatim, unmodified.
function variationsSubsection(rowsWithVariations) {
  return `<div class="variations-subsection-label">Variations / Addendums</div>${rowsWithVariations.map(r => variationBlock(r)).join('')}`
}

/**
 * Detailed IPC Statement — renders buildIpcStatementRows() output
 * exactly as computed, no recalculation. Columns match the existing
 * on-screen Statement modal one-to-one (ipc_number, gross, retention,
 * net_payable, cumulative_net, cumulative_retention, paid_this_ipc,
 * cumulative_paid_linked, balance_linked), plus submission_date as the
 * "IPC Date" column requested in the approved spec.
 *
 * The final reconciliation row makes the audited guarantee visible on
 * the printed page itself: the last IPC's cumulative_net (read
 * directly from ipcData, not recomputed) is placed next to
 * contractRow.certified (from computeContractFinancials(), via
 * buildSubcontractReportRows()) — these two numbers are expected to
 * match exactly for every contract, by construction (see the audit
 * this feature was built from).
 */
function ipcStatementSubsection(contractRow, ipcData) {
  if (!ipcData || ipcData.rows.length === 0) {
    return `
      <div class="ipc-statement-subsection">
        <div class="detail-subsection-title">Detailed IPC Statement</div>
        <div class="empty-note" style="padding:10px 0">No approved/paid IPCs recorded for this contract.</div>
      </div>`
  }
  const lastRow = ipcData.rows[ipcData.rows.length - 1]
  // Reconciliation fix: cumulative_net (from IPC statement rows) is a
  // NET figure (Σ net_payable, i.e. after retention deduction) — it
  // must be compared against contractRow.net_payable (also Net, from
  // computeContractFinancials()'s cumulative_net_payable), NOT
  // contractRow.certified (which is Gross, Σ gross_amount, before
  // retention). Comparing Net against Gross was the actual bug behind
  // the false "Difference detected" warning — not a real data
  // discrepancy.
  const reconciles = Math.abs(lastRow.cumulative_net - contractRow.net_payable) < 0.01
  return `
      <div class="ipc-statement-subsection">
        <div class="detail-subsection-title">Detailed IPC Statement</div>
        <table class="data-table ipc-table">
          <colgroup>
            <col style="width:8%"><col style="width:11%"><col style="width:13%">
            <col style="width:12%"><col style="width:13%"><col style="width:13%">
            <col style="width:13%"><col style="width:17%">
          </colgroup>
          <thead>
            <tr>
              <th class="al">IPC #</th><th class="al">IPC Date</th><th>Gross</th>
              <th>Retention</th><th>Net Payable</th><th>Cumulative Net</th>
              <th>Paid (This IPC)</th><th>Balance (Linked)</th>
            </tr>
          </thead>
          <tbody>
            ${ipcData.rows.map(ipc => `
            <tr>
              <td class="al blue">#${escapeHtml(String(ipc.ipc_number))}</td>
              <td class="al">${fmtDate(ipc.submission_date)}</td>
              <td class="mono">${fmt(ipc.gross)}</td>
              <td class="mono">${fmt(ipc.retention)}</td>
              <td class="mono">${fmt(ipc.net_payable)}</td>
              <td class="mono bold">${fmt(ipc.cumulative_net)}</td>
              <td class="mono blue">${ipc.paid_this_ipc > 0 ? fmt(ipc.paid_this_ipc) : '—'}</td>
              <td class="mono ${ipc.balance_linked > 0 ? 'red' : ''}">${fmt(ipc.balance_linked)}</td>
            </tr>`).join('')}
          </tbody>
        </table>
        ${ipcData.unlinkedTotal > 0 ? `<div class="ipc-note">&#9888; SAR ${fmt(ipcData.unlinkedTotal)} in payments on this contract are not linked to a specific IPC — Paid/Balance figures above reflect linked payments only.</div>` : ''}
        <div class="ipc-reconciliation-row">
          <span>Cumulative Net (last IPC): <b class="mono">${fmt(lastRow.cumulative_net)}</b></span>
          <span>Summary Net Payable: <b class="mono">${fmt(contractRow.net_payable)}</b></span>
          <span class="${reconciles ? 'ipc-reconciles-ok' : 'ipc-reconciles-warn'}">${reconciles ? '&#10003; Reconciles' : '&#9888; Difference detected'}</span>
        </div>
      </div>`
}

// A handful of print-only classes not already in buildCss() — kept
// separate and minimal rather than duplicating the ~200 lines already
// defined there.
function extraCss() {
  return `
    .variations-subsection-label { font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #6B7280; margin: 8px 0 4px; }
    .ipc-statement-subsection { margin-top: 8px; break-inside: avoid; }
    .ipc-table { font-size: 8.5px; }
    .ipc-note { font-size: 7.5px; color: #D97706; background: #FFFBEB; padding: 5px 8px; border-radius: 4px; margin-top: 4px; }
    .ipc-reconciliation-row { display: flex; gap: 16px; align-items: center; font-size: 8px; color: #6B7280; background: #F9FAFB; border-radius: 4px; padding: 5px 10px; margin-top: 4px; flex-wrap: wrap; }
    .ipc-reconciles-ok { color: #1A7A4A; font-weight: 700; margin-left: auto; }
    .ipc-reconciles-warn { color: #D97706; font-weight: 700; margin-left: auto; }
    /* Consolidated Financial Summary table — smaller font/padding than
       the base .data-table, specifically because this table can carry
       large SAR amounts (up to 9+ digits) across SIX numeric columns
       simultaneously on a project with hundreds of contracts — the
       base 9.5px sizing (fine for the main Subcontract Report's
       narrower, fewer-column tables) was overflowing A4's printable
       width here. Column widths unchanged in proportion, only the
       font/padding shrank to fit within them reliably. */
    table.data-table.consolidated-table { font-size: 7.8px; width: auto; }
    .consolidated-table th { font-size: 7px; padding: 5px 5px; }
    .consolidated-table td { padding: 4px 5px; }
  `
}
